import { useState, useRef } from "react";
import { useAuth } from "@/context/AuthContext";
import { Navigate, Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Upload, Wand2, Download, ArrowLeft, RefreshCw, Sparkles, Check, X, ImageIcon, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";

const MAX_SLOTS = 20;
let uid = 0;
const nextId = () => ++uid;

const newSlot = (label = "") => ({
  id: nextId(),
  label,
  file: null,
  preview: null,
  status: "idle",
  result: null,
  resultMime: "image/png",
  error: null,
});

export default function AdminPhotoStudio() {
  const { user, loading } = useAuth();
  const [slots, setSlots] = useState(() => Array.from({ length: 3 }, (_, i) => newSlot(`Bild ${i + 1}`)));
  const [running, setRunning] = useState(false);
  const fileRefs = useRef({});
  const addRef = useRef(null);

  if (loading) return null;
  if (!user) return <Navigate to="/admin/login" replace />;

  const patchSlot = (id, patch) => {
    setSlots((prev) => prev.map((s) => (s.id === id ? { ...s, ...patch } : s)));
  };

  const addSlots = (count = 1) => {
    setSlots((prev) => {
      const remaining = MAX_SLOTS - prev.length;
      const n = Math.min(count, remaining);
      if (n <= 0) {
        toast.error(`Maximal ${MAX_SLOTS} Bilder`);
        return prev;
      }
      const startIdx = prev.length;
      return [...prev, ...Array.from({ length: n }, (_, i) => newSlot(`Bild ${startIdx + i + 1}`))];
    });
  };

  const removeSlot = (id) => setSlots((prev) => prev.filter((s) => s.id !== id));

  const handleFile = (id, f) => {
    if (!f) return;
    if (!f.type.startsWith("image/")) {
      toast.error("Bitte ein Bild auswählen");
      return;
    }
    if (f.size > 12 * 1024 * 1024) {
      toast.error("Bild zu groß (max. 12 MB)");
      return;
    }
    const reader = new FileReader();
    reader.onload = (e) =>
      patchSlot(id, { file: f, preview: e.target.result, status: "idle", result: null, error: null });
    reader.readAsDataURL(f);
  };

  const handleBulkUpload = (fileList) => {
    const files = Array.from(fileList || []).filter((f) => f.type.startsWith("image/"));
    if (files.length === 0) return;

    setSlots((prev) => {
      const next = [...prev];
      let filesLeft = [...files];
      // Fill empty slots first
      for (let i = 0; i < next.length && filesLeft.length; i++) {
        if (!next[i].file) {
          const f = filesLeft.shift();
          const preview = URL.createObjectURL(f);
          next[i] = { ...next[i], file: f, preview, status: "idle", result: null, error: null };
        }
      }
      // Add new slots for remaining files
      while (filesLeft.length && next.length < MAX_SLOTS) {
        const f = filesLeft.shift();
        const preview = URL.createObjectURL(f);
        next.push({ ...newSlot(`Bild ${next.length + 1}`), file: f, preview });
      }
      if (filesLeft.length) toast.error(`${filesLeft.length} Bild(er) konnten nicht hinzugefügt werden (max. ${MAX_SLOTS})`);
      return next;
    });
    if (addRef.current) addRef.current.value = "";
  };

  const clearSlot = (id) => {
    patchSlot(id, { file: null, preview: null, status: "idle", result: null, error: null });
    if (fileRefs.current[id]) fileRefs.current[id].value = "";
  };

  const processSlot = async (slot) => {
    if (!slot?.file) return;
    patchSlot(slot.id, { status: "processing", error: null });
    try {
      const fd = new FormData();
      fd.append("image", slot.file);
      fd.append("mode", "auto");
      const res = await api.post("/admin/photo-studio/process", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 180000,
      });
      patchSlot(slot.id, { status: "done", result: res.data.data, resultMime: res.data.mime_type || "image/png" });
    } catch (err) {
      patchSlot(slot.id, { status: "error", error: formatApiError(err) });
    }
  };

  const processAll = async () => {
    const toProcess = slots.filter((s) => s.file && s.status !== "done");
    if (toProcess.length === 0) {
      toast.error("Keine Bilder zum Bearbeiten");
      return;
    }
    setRunning(true);
    setSlots((prev) => prev.map((s) => (toProcess.find((t) => t.id === s.id) ? { ...s, status: "queued" } : s)));
    toast.success(`Bearbeite ${toProcess.length} Bild${toProcess.length === 1 ? "" : "er"} — kann ein paar Minuten dauern`);
    for (const slot of toProcess) {
      const current = slots.find((s) => s.id === slot.id) || slot;
      await processSlot(current);
    }
    setRunning(false);
    toast.success("Batch-Bearbeitung fertig");
  };

  const downloadOne = (slot) => {
    if (!slot.result) return;
    const a = document.createElement("a");
    a.href = `data:${slot.resultMime};base64,${slot.result}`;
    const safe = (slot.label || `bild-${slot.id}`).replace(/[^a-z0-9äöüß_-]+/gi, "_");
    a.download = `${safe}_dr-automobile.png`;
    a.click();
  };

  const downloadAll = () => {
    slots.filter((s) => s.result).forEach((s, i) => setTimeout(() => downloadOne(s), i * 250));
  };

  const uploadedCount = slots.filter((s) => s.file).length;
  const doneCount = slots.filter((s) => s.status === "done").length;
  const errorCount = slots.filter((s) => s.status === "error").length;
  const queuedCount = slots.filter((s) => s.status === "queued" || s.status === "processing").length;

  return (
    <div className="bg-slate-50 min-h-[calc(100vh-128px)]" data-testid="photo-studio-page">
      <div className="border-b border-slate-200 bg-white sticky top-32 z-40">
        <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12 py-5">
          <Link to="/admin" className="inline-flex items-center gap-2 text-sm text-slate-600 hover:text-slate-900 mb-2">
            <ArrowLeft size={14} /> Zurück zum Admin
          </Link>
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div>
              <div className="text-[10px] uppercase tracking-[0.3em] text-slate-500 mb-1">
                <span className="divider-thin" />KI-Werkzeug
              </div>
              <h1 className="font-heading font-bold text-2xl md:text-3xl text-slate-900">Foto-Studio</h1>
              <p className="text-sm text-slate-600 mt-1">
                Beliebige Fahrzeugfotos — ganzes Auto, Felge, Detail oder Innenraum — in den D.R Automobile Showroom platzieren.
              </p>
            </div>
            <div className="flex items-center gap-3 flex-wrap">
              <div className="text-xs text-slate-600 bg-slate-100 px-3 py-2 border border-slate-200">
                <span data-testid="counter-uploaded">{uploadedCount}</span> hochgeladen ·{" "}
                <span className="text-emerald-700 font-medium" data-testid="counter-done">{doneCount}</span> fertig
                {errorCount > 0 && <> · <span className="text-red-600 font-medium">{errorCount}</span> Fehler</>}
              </div>
              {doneCount > 0 && (
                <button
                  onClick={downloadAll}
                  data-testid="download-all"
                  className="inline-flex items-center gap-2 border border-slate-900 text-slate-900 px-4 py-2 text-sm font-medium hover:bg-slate-900 hover:text-white transition-crisp"
                >
                  <Download size={14} /> Alle herunterladen
                </button>
              )}
              <button
                onClick={processAll}
                disabled={running || uploadedCount === 0}
                data-testid="process-all"
                className="inline-flex items-center gap-2 bg-slate-900 text-white px-5 py-2 text-sm font-medium hover:bg-amber-500 hover:text-slate-900 transition-crisp disabled:opacity-40 disabled:cursor-not-allowed"
              >
                {running ? (
                  <><RefreshCw size={14} className="animate-spin" /> Läuft … ({queuedCount} offen)</>
                ) : (
                  <><Wand2 size={14} /> Alle KI-bearbeiten</>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12 py-8">
        {/* Bulk-drop area */}
        <label
          onDragOver={(e) => e.preventDefault()}
          onDrop={(e) => { e.preventDefault(); handleBulkUpload(e.dataTransfer.files); }}
          className="block bg-white border-2 border-dashed border-slate-300 hover:border-slate-900 cursor-pointer p-6 mb-6 text-center transition-crisp"
          data-testid="bulk-upload"
        >
          <input
            ref={addRef}
            type="file"
            accept="image/*"
            multiple
            className="hidden"
            onChange={(e) => handleBulkUpload(e.target.files)}
          />
          <Upload size={22} strokeWidth={1.5} className="mx-auto text-slate-500 mb-2" />
          <p className="text-sm font-medium text-slate-900">Mehrere Bilder gleichzeitig hochladen</p>
          <p className="text-xs text-slate-500 mt-1">
            Wird automatisch in freie Kacheln verteilt (max. {MAX_SLOTS} · JPG/PNG · max. 12 MB pro Bild)
          </p>
        </label>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
          {slots.map((slot) => (
            <div key={slot.id} className="bg-white border border-slate-200 overflow-hidden flex flex-col" data-testid={`slot-${slot.id}`}>
              <div className="flex items-center gap-2 px-3 py-2.5 border-b border-slate-100">
                <input
                  type="text"
                  value={slot.label}
                  onChange={(e) => patchSlot(slot.id, { label: e.target.value })}
                  placeholder="Bezeichnung (z. B. Felge)"
                  className="flex-1 text-sm font-medium text-slate-900 bg-transparent focus:outline-none border-b border-transparent focus:border-slate-300 py-0.5"
                  data-testid={`slot-label-${slot.id}`}
                />
                <StatusPill status={slot.status} />
                <button
                  type="button"
                  onClick={() => removeSlot(slot.id)}
                  className="text-slate-300 hover:text-red-600 p-1"
                  data-testid={`slot-remove-${slot.id}`}
                  title="Kachel entfernen"
                >
                  <Trash2 size={13} />
                </button>
              </div>

              <div className="flex-1 flex flex-col">
                {!slot.file ? (
                  <label
                    onDragOver={(e) => e.preventDefault()}
                    onDrop={(e) => { e.preventDefault(); handleFile(slot.id, e.dataTransfer.files?.[0]); }}
                    className="flex-1 flex flex-col items-center justify-center p-6 cursor-pointer hover:bg-slate-50 transition-crisp border-2 border-dashed border-slate-200 m-3 min-h-[160px]"
                    data-testid={`upload-${slot.id}`}
                  >
                    <input
                      ref={(el) => (fileRefs.current[slot.id] = el)}
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={(e) => handleFile(slot.id, e.target.files?.[0])}
                    />
                    <Upload size={22} strokeWidth={1.5} className="text-slate-400 mb-2" />
                    <p className="text-xs text-slate-600 text-center">Bild hochladen oder ziehen</p>
                  </label>
                ) : (
                  <>
                    <div className="grid grid-cols-2 gap-px bg-slate-100">
                      <div className="aspect-square bg-slate-100 relative">
                        <img src={slot.preview} alt="Original" className="w-full h-full object-cover" />
                        <span className="absolute top-1 left-1 bg-slate-900/70 text-white text-[9px] uppercase tracking-widest px-1.5 py-0.5">Original</span>
                      </div>
                      <div className="aspect-square bg-slate-100 relative flex items-center justify-center">
                        {slot.status === "processing" && (
                          <RefreshCw size={24} className="text-slate-400 animate-spin" />
                        )}
                        {slot.status === "queued" && (
                          <span className="text-[10px] text-slate-500">In Warteschlange…</span>
                        )}
                        {slot.status === "error" && (
                          <div className="p-2 text-center">
                            <X size={20} className="text-red-500 mx-auto mb-1" />
                            <p className="text-[10px] text-red-600 leading-tight">{slot.error || "Fehler"}</p>
                          </div>
                        )}
                        {slot.status === "done" && slot.result && (
                          <>
                            <img
                              src={`data:${slot.resultMime};base64,${slot.result}`}
                              alt="Ergebnis"
                              className="w-full h-full object-cover"
                              data-testid={`result-${slot.id}`}
                            />
                            <span className="absolute top-1 left-1 bg-amber-500 text-slate-900 text-[9px] uppercase tracking-widest px-1.5 py-0.5 font-semibold">Studio</span>
                          </>
                        )}
                        {slot.status === "idle" && (
                          <div className="text-center p-3">
                            <ImageIcon size={20} className="text-slate-300 mx-auto mb-1" />
                            <p className="text-[10px] text-slate-400">Noch nicht bearbeitet</p>
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="px-3 py-2.5 flex items-center justify-between gap-2 border-t border-slate-100 mt-auto">
                      <button
                        onClick={() => clearSlot(slot.id)}
                        className="text-[11px] text-slate-500 hover:text-red-600 flex items-center gap-1"
                        data-testid={`clear-${slot.id}`}
                      >
                        <RefreshCw size={11} /> Tauschen
                      </button>
                      {slot.status === "done" ? (
                        <button
                          onClick={() => downloadOne(slot)}
                          data-testid={`dl-${slot.id}`}
                          className="text-[11px] bg-slate-900 text-white px-3 py-1.5 hover:bg-amber-500 hover:text-slate-900 transition-crisp flex items-center gap-1"
                        >
                          <Download size={11} /> Download
                        </button>
                      ) : (
                        <button
                          onClick={() => processSlot(slot)}
                          disabled={slot.status === "processing" || slot.status === "queued"}
                          data-testid={`process-${slot.id}`}
                          className="text-[11px] border border-slate-300 text-slate-700 px-3 py-1.5 hover:border-slate-900 hover:text-slate-900 transition-crisp flex items-center gap-1 disabled:opacity-40"
                        >
                          <Wand2 size={11} /> Einzeln
                        </button>
                      )}
                    </div>
                  </>
                )}
              </div>
            </div>
          ))}

          {slots.length < MAX_SLOTS && (
            <button
              onClick={() => addSlots(1)}
              data-testid="add-slot"
              className="border-2 border-dashed border-slate-300 hover:border-slate-900 hover:bg-white p-6 min-h-[220px] flex flex-col items-center justify-center transition-crisp text-slate-500 hover:text-slate-900"
            >
              <Plus size={24} strokeWidth={1.5} className="mb-2" />
              <span className="text-sm font-medium">Kachel hinzufügen</span>
              <span className="text-[10px] text-slate-400 mt-1">{slots.length} / {MAX_SLOTS}</span>
            </button>
          )}
        </div>

        <div className="mt-8 bg-amber-50 border border-amber-200 p-4 text-xs text-amber-900 leading-relaxed">
          <strong>So funktioniert es:</strong> Lade beliebige Fotos hoch — ganzes Auto, nur eine Felge, das
          Cockpit, den Kofferraum, Detailaufnahmen. Die KI erkennt selbst, was zu sehen ist, behält das
          Motiv <em>exakt</em> so wie fotografiert und ersetzt nur den Hintergrund mit unserem D.R Automobile
          Showroom. Vergib pro Kachel einen Namen (wird zum Dateinamen). Bis zu {MAX_SLOTS} Bilder pro Batch.
        </div>
      </div>
    </div>
  );
}

function StatusPill({ status }) {
  const map = {
    idle:       { label: "Bereit",       cls: "bg-slate-100 text-slate-600", icon: null },
    queued:     { label: "Warteschlange",cls: "bg-amber-100 text-amber-800", icon: <RefreshCw size={10} /> },
    processing: { label: "Bearbeite…",   cls: "bg-blue-100 text-blue-800",   icon: <RefreshCw size={10} className="animate-spin" /> },
    done:       { label: "Fertig",       cls: "bg-emerald-100 text-emerald-800", icon: <Check size={10} /> },
    error:      { label: "Fehler",       cls: "bg-red-100 text-red-700",     icon: <X size={10} /> },
  };
  const s = map[status] || map.idle;
  return (
    <span className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-widest px-2 py-0.5 ${s.cls}`}>
      {s.icon} {s.label}
    </span>
  );
}
